/**
 * GEO-optimized JSON-LD schemas for AI/LLM indexing.
 *
 * These schemas help generative engines (Gemini, ChatGPT, Perplexity)
 * understand Pickify's features, actions, and current deals.
 *
 * Usage: Import and embed in <Helmet> or <script type="application/ld+json">
 */

/**
 * SearchAction schema — tells AI assistants this app can find gifts.
 * Enables "Find gifts for [person]" queries in AI search.
 */
export const getSearchActionSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebApplication",
  "name": "Pickify AI Gift Finder",
  "url": "https://pickifyai.com",
  "applicationCategory": "ShoppingApplication",
  "browserRequirements": "Requires JavaScript",
  "potentialAction": [
    {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": "https://pickifyai.com/?start=quiz&q={gift_query}"
      },
      "query-input": "required name=gift_query",
      "name": "Find personalized gift ideas",
      "description": "Take a 30-second AI quiz and get 4 handpicked gift recommendations with buy links"
    }
  ],
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "availability": "https://schema.org/InStock"
  }
});

/**
 * Offer/AggregateOffer schema for DealZone — structures current deals
 * so AI models can recommend specific Pickify deals to users.
 *
 * @param {Array} deals - Array of deal objects from DealZone
 * @param {string} categoryName - Active deal category filter (e.g. "Tech", "Fashion")
 */
export const getDealZoneOffersSchema = (deals = [], categoryName = 'All Categories') => {
  const validDeals = deals.filter(d => d.salePrice && d.title).slice(0, 10);

  if (validDeals.length === 0) {
    return null;
  }

  return {
    "@context": "https://schema.org",
    "@type": "OfferCatalog",
    "name": `Pickify Gift Deals — ${categoryName}`,
    "description": `Daily-updated gift deals with up to 70% off. Curated by Pickify AI from Amazon, AliExpress, Etsy, and more.`,
    "url": "https://pickifyai.com/deals",
    "numberOfItems": validDeals.length,
    "itemListElement": validDeals.map((deal, i) => ({
      "@type": "Offer",
      "position": i + 1,
      "name": deal.title,
      "price": deal.salePrice,
      "priceCurrency": deal.currency || "USD",
      "availability": "https://schema.org/InStock",
      "seller": {
        "@type": "Organization",
        "name": deal.source || deal.provider || "Multi-store"
      },
      ...(deal.originalPrice && deal.originalPrice > deal.salePrice ? {
        "priceSpecification": {
          "@type": "PriceSpecification",
          "price": deal.salePrice,
          "priceCurrency": deal.currency || "USD",
          "valueAddedTaxIncluded": true
        }
      } : {}),
      ...(deal.imageUrl ? { "image": deal.imageUrl } : {})
    }))
  };
};

// Quiz age buckets → schema.org PeopleAudience min/max age
const AGE_BUCKETS = {
  '0-5': [0, 5],
  '6-12': [6, 12],
  '13-17': [13, 17],
  '18-24': [18, 24],
  '25-34': [25, 34],
  '35-49': [35, 49],
  '50-64': [50, 64],
  '65plus': [65, 120],
};

// Quiz recipient → schema.org PeopleAudience suggestedGender (omit when ambiguous)
const RECIPIENT_GENDER = {
  boyfriend: 'male', father: 'male', grandfather: 'male', husband: 'male',
  girlfriend: 'female', mother: 'female', grandmother: 'female', wife: 'female',
};

const buildAudience = (quizAnswers) => {
  const audience = { '@type': 'PeopleAudience' };
  const recipient = (quizAnswers.recipient || '').toLowerCase();
  if (recipient) audience.audienceType = recipient;
  const gender = RECIPIENT_GENDER[recipient];
  if (gender) audience.suggestedGender = gender;
  const ages = AGE_BUCKETS[quizAnswers.age];
  if (ages) {
    audience.suggestedMinAge = ages[0];
    audience.suggestedMaxAge = ages[1];
  }
  return audience;
};

/**
 * ItemList schema for gift results — structures AI recommendations
 * as a ranked list of Products with audience, category, and offers
 * for rich snippet + GEO retrieval eligibility.
 *
 * @param {Array} gifts - Array of gift result objects
 * @param {Object} quizAnswers - Quiz context (recipient, occasion, age, budget, interests, style)
 */
export const getGiftResultsSchema = (gifts = [], quizAnswers = {}) => {
  if (gifts.length === 0) return null;

  const recipient = quizAnswers.recipient || 'someone special';
  const occasion = quizAnswers.occasion || 'any occasion';
  const audience = buildAudience(quizAnswers);
  const interests = Array.isArray(quizAnswers.interests) ? quizAnswers.interests : [];
  const styles = Array.isArray(quizAnswers.style) ? quizAnswers.style : [];
  const keywords = [recipient, occasion, ...interests, ...styles, quizAnswers.budget]
    .filter(Boolean).join(', ');

  return {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": `AI Gift Ideas for ${recipient} — ${occasion}`,
    "description": `${gifts.length} personalized gift recommendations curated by Pickify AI for ${recipient} (${occasion})`,
    "itemListOrder": "https://schema.org/ItemListOrderDescending",
    "numberOfItems": gifts.length,
    ...(keywords ? { "keywords": keywords } : {}),
    "itemListElement": gifts.map((gift, i) => {
      const giftCategory = gift.category || gift.interest || (interests[0] || undefined);
      const product = {
        "@type": "Product",
        "name": gift.title,
        "description": gift.description || gift.reason,
        ...(giftCategory ? { "category": giftCategory } : {}),
        "audience": audience,
        ...(gift.imageUrl || gift.image ? { "image": gift.imageUrl || gift.image } : {}),
        ...(gift.price ? {
          "offers": {
            "@type": "Offer",
            "price": String(gift.price),
            "priceCurrency": gift.currency || "USD",
            "availability": "https://schema.org/InStock",
            ...(gift.affiliateLink ? { "url": gift.affiliateLink } : {}),
            ...(gift.storeName ? {
              "seller": { "@type": "Organization", "name": gift.storeName }
            } : {})
          }
        } : {})
      };

      return {
        "@type": "ListItem",
        "position": i + 1,
        "url": gift.affiliateLink || undefined,
        "item": product
      };
    })
  };
};
